import { NavigateRowAction } from '@app/components/table/NavigateRowAction';
import { Box, TableRowProps } from '@mui/material';
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowData,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useEffect, useMemo, useRef } from 'react';
import useRouter from 'use-react-router';
import { ResultsCount } from '../results/ResultsCount';
import { useMrtLocale } from './useMrtLocale';

/** Legacy column shape (Header, accessor as render fn, align, w) supported at runtime by the normalizer. */
export type LegacyMRTColumnDef<TData extends MRT_RowData> = {
  id?: string;
  Header?: string;
  header?: string;
  accessor?: ((row: TData) => unknown) | string;
  align?: 'left' | 'center' | 'right';
  w?: number;
  [key: string]: unknown;
};

type Props<TData extends MRT_RowData> = Omit<MRT_TableOptions<TData>, 'columns'> & {
  columns: (MRT_ColumnDef<TData, any> | LegacyMRTColumnDef<TData>)[];
  memoizeColumns?: boolean;
  navigateToOnClick?: (id: string, data: TData) => string;
  enableNavigateAction?: boolean;
  /**
   * Rows currently shown in the table body (after filter, sort, and pagination).
   * Use this for actions that should follow visible order (e.g. dialog prev/next).
   */
  onDisplayRowsChange?: (rows: TData[]) => void;
};

export function MrtTable<TData extends MRT_RowData>({
  columns,
  memoizeColumns = typeof import.meta === 'undefined' ? true : !import.meta.env.DEV,
  navigateToOnClick,
  enableNavigateAction,
  onDisplayRowsChange,
  ...p
}: Props<TData>) {
  const { history } = useRouter();
  const mrtLocalization = useMrtLocale();
  const memoColumns = useMemo(() => columns, memoizeColumns ? [] : [columns]);

  /** Normalize legacy React Table v7 / custom defs (Header, accessor as render) to MRT/TanStack v8 (header, accessorFn, Cell) so the table never sees undefined header/id. */
  const normalizedColumns = useMemo(() => {
    const raw = memoColumns ?? [];
    if (!Array.isArray(raw) || raw.length === 0) return raw;
    return raw.map((col, i) => {
      const c = col as unknown as Record<string, unknown>;
      const header = c.header ?? c.Header;
      const accessor = c.accessor;
      const isAccessorRenderFn = typeof accessor === 'function' && accessor.length <= 1;
      return {
        ...col,
        id: c.id ?? c.accessorKey ?? header ?? `col_${i}`,
        header:
          typeof header === 'string' ? header : (header ?? (c.Header as string) ?? `Col ${i}`),
        ...(isAccessorRenderFn && {
          Cell: ({ row }: { row: MRT_Row<TData> }) =>
            (accessor as (row: TData) => unknown)(row.original),
          accessorFn: () => null,
        }),
        ...(typeof accessor === 'function' &&
          !isAccessorRenderFn && {
            accessorFn: accessor as (row: TData) => unknown,
          }),
      } as MRT_ColumnDef<TData, unknown>;
    });
  }, [memoColumns]);

  const {
    initialState,
    enableRowActions,
    positionActionsColumn,
    displayColumnDefOptions,
    renderRowActions,
    ...rest
  } = p;

  const defaultMuiTableBodyRowProps: Partial<TableRowProps> = {
    sx: {},
  };

  const muiTableBodyRowProps = (props: { row: MRT_Row<TData> }) => {
    const baseProps =
      typeof rest.muiTableBodyRowProps === 'function'
        ? {
            ...rest.muiTableBodyRowProps?.(props as any),
            ...defaultMuiTableBodyRowProps,
          }
        : {
            ...rest.muiTableBodyRowProps,
            ...defaultMuiTableBodyRowProps,
          };

    if (navigateToOnClick) {
      (baseProps.sx as any).cursor = 'pointer';
    }

    const onMouseDown = navigateToOnClick
      ? (e: React.MouseEvent<HTMLTableRowElement>) => {
          // middle click (button === 1)
          if (e.button === 1) {
            window.open(
              navigateToOnClick(props.row.original.id, props.row.original),
              '_blank',
              'noopener,noreferrer',
            );
          }
        }
      : (rest.muiTableBodyRowProps as any)?.onMouseDown;

    return {
      onClick: navigateToOnClick
        ? (event: React.MouseEvent<HTMLTableRowElement>) => {
            const selection = window.getSelection();
            const isTextSelected = selection && selection.toString().length > 0;
            if (isTextSelected) {
              return;
            }

            history.push(navigateToOnClick(props.row.original.id, props.row.original));
          }
        : undefined,
      onMouseDown,
      ...baseProps,
    };
  };

  // Auto-configure row actions if enableNavigateAction is true and navigateToOnClick exists
  const shouldEnableNavigateAction = enableNavigateAction && navigateToOnClick;
  const finalEnableRowActions = shouldEnableNavigateAction ? true : enableRowActions;
  const finalPositionActionsColumn = shouldEnableNavigateAction ? 'last' : positionActionsColumn;

  const defaultNavigateActionColumnOptions = {
    header: '',
    size: 20,
    enableColumnFilter: false,
    muiTableHeadCellProps: {
      sx: {
        justifyContent: 'flex-end',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        justifyContent: 'flex-end',
      },
    },
  };

  const finalDisplayColumnDefOptions = shouldEnableNavigateAction
    ? {
        ...displayColumnDefOptions,
        'mrt-row-actions': {
          ...defaultNavigateActionColumnOptions,
          ...displayColumnDefOptions?.['mrt-row-actions'],
        },
      }
    : displayColumnDefOptions;

  const finalRenderRowActions = shouldEnableNavigateAction
    ? ({ row }: { row: MRT_Row<TData> }) => {
        const url = navigateToOnClick(row.original.id, row.original);
        return <NavigateRowAction to={url} />;
      }
    : renderRowActions;

  const columnsForTable = normalizedColumns ?? [];
  const dataForTable = (rest as { data?: TData[] }).data ?? [];
  const table = useMaterialReactTable({
    layoutMode: 'grid',
    localization: mrtLocalization,
    muiPaginationProps: {
      rowsPerPageOptions: [20, 50, 100],
      showFirstButton: true,
      showLastButton: true,
    },
    initialState: {
      ...MrtTable.defaultInitialState,
      ...initialState,
    },
    columnFilterDisplayMode: 'popover',
    paginationDisplayMode: 'pages',
    enableHiding: false,
    defaultColumn: {
      enableColumnFilter: false,
      enableColumnActions: false,
      enableSorting: false,
    },
    enableDensityToggle: false,
    enableGlobalFilter: false,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 500,
        fontSize: '0.875rem',
        '& .MuiInputBase-root': {
          marginTop: 0.5,
          fontWeight: 400,
          fontSize: '13px',
          textTransform: 'none',
        },
      },
    },
    muiTableContainerProps: {
      id: 'mrt-scroll-container',
    },
    renderTopToolbarCustomActions:
      rest.positionPagination === 'both'
        ? undefined
        : ({ table }) => {
            return (
              <Box p={1}>
                <ResultsCount
                  noCustomEmptyState
                  noMargin
                  count={p.rowCount || table.getFilteredRowModel().rows.length}
                />
              </Box>
            );
          },
    enableRowActions: finalEnableRowActions,
    positionActionsColumn: finalPositionActionsColumn,
    displayColumnDefOptions: finalDisplayColumnDefOptions,
    renderRowActions: finalRenderRowActions,
    ...rest,
    data: dataForTable,
    columns: columnsForTable as MRT_ColumnDef<TData, any>[],
    muiTableBodyRowProps,
  });

  const { sorting, pagination, columnFilters, globalFilter } = table.getState();
  const prevDisplayRowsRef = useRef<TData[] | null>(null);

  // Notify consumers of the current page rows in display order (sorted/filtered/paginated).
  useEffect(() => {
    if (!onDisplayRowsChange) {
      return;
    }

    const next = table.getRowModel().rows.map((row) => row.original);
    const prev = prevDisplayRowsRef.current;
    if (prev && prev.length === next.length && prev.every((row, index) => row === next[index])) {
      return;
    }

    prevDisplayRowsRef.current = next;
    onDisplayRowsChange(next);
  }, [
    // table identity is stable from useMaterialReactTable; state slices drive updates.
    table,
    onDisplayRowsChange,
    dataForTable,
    sorting,
    pagination,
    columnFilters,
    globalFilter,
  ]);

  return <MaterialReactTable table={table} />;
}

MrtTable.defaultInitialState = {
  pagination: {
    pageSize: 50,
    pageIndex: 0,
  },
};

export const Table = MrtTable;
